import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { CLAIM_STATUSES } from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'
import type { MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import type { Translate } from '../status-labels.js'
import { DataSurface } from './DataSurface.js'
import { formatWireForDisplay, type WireMoney } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * The monthly statement a panel is sent. §6.13 `CLM-001` to `CLM-006`; §15.20 step 4: "Monthly
 * statement generated per panel, listing every order."
 *
 * > `V1-SPEC.md` §6.13: "**Capture only.** Panel and corporate accounts. No electronic gateways"
 *
 * So this screen issues and reads statements and sends nothing. A shopkeeper prints what it shows,
 * or reads the figures off it over the phone to the panel's accounts clerk.
 *
 * ---
 *
 * **Two questions, which is why both halves are here.** "What have we sent Acme Textiles" is the
 * question a shop has when the clerk rings up about September, and it is not the lesser half: a
 * screen that only offers the next action cannot answer it. An issued statement is frozen and its
 * revision is on it, so the list is also the answer to "which copy are they holding".
 *
 * **It pre-checks nothing itself, and that is deliberate.** `VoidSale.tsx` sets out the rule: a
 * refused write sits `blocked` and holds every write queued behind it (`OFF-001a`, `OFF-005`), so
 * one refused statement on a till stops the till. Two of this route's refusals cannot be derived
 * from anything this app can see, whether a month has any claims at all and whether they reach a
 * branch the session does not hold, so `GET /panels/:panelId/statements` answers with an offer per
 * month and this renders that answer. A month is either offered or shown withheld with the
 * server's own sentence. The only refusal nobody can pre-check is somebody else's reissue landing
 * between this read and the write, which is `STATEMENT_NOT_THE_LATEST` and inherently a race.
 *
 * **A reissue is offered where a revision already exists, never an edit.** The panel is holding a
 * copy of what was sent, so a correction is a new revision pointing at the one it replaces and the
 * replaced one stays on this list. The id it supersedes comes from the offer rather than from
 * anything typed, so the screen and the server cannot disagree about which copy was on screen.
 */

// ---------------------------------------------------------------------------------------------
// What the read sends. Shaped to `statements.service.ts`, not imported from it: `apps/*` never
// import from each other.
// ---------------------------------------------------------------------------------------------

export interface PanelOption {
  readonly id: string
  readonly name: string
  readonly currency: string
  readonly deactivated: boolean
}

export interface StatementLineData {
  readonly claimId: string
  readonly orderId: string
  readonly capturedOn: string
  /** The claim's state when the statement was issued, not now. */
  readonly claimStatus: string
  readonly panelShare: WireMoney
  readonly customerShare: WireMoney
  readonly paid: WireMoney
  readonly outstanding: WireMoney
  readonly denied: WireMoney
  readonly transferred: WireMoney
  readonly writtenOff: WireMoney
}

export interface StatementTotalsData {
  readonly panelShare: WireMoney
  readonly customerShare: WireMoney
  readonly paid: WireMoney
  readonly outstanding: WireMoney
  readonly denied: WireMoney
  readonly transferred: WireMoney
  readonly writtenOff: WireMoney
}

export interface StatementData {
  readonly id: string
  readonly periodFrom: string
  readonly periodTo: string
  readonly revision: number
  readonly superseded: boolean
  readonly issuedAt: string
  readonly totals: StatementTotalsData
  readonly lines: readonly StatementLineData[]
}

export interface StatementOfferData {
  /** `YYYY-MM`. */
  readonly month: string
  readonly claims: number
  readonly offered: boolean
  readonly revision: number
  /** Present when a revision is live: issuing this month is a reissue of that one. */
  readonly supersedesStatementId?: string
  readonly code?: string
  readonly message?: string
}

export interface PanelStatementsData {
  readonly panelId: string
  readonly panel: string
  readonly currency: string
  readonly statements: readonly StatementData[]
  readonly withheld: number
  readonly offers: readonly StatementOfferData[]
}

export interface PanelStatementsProps {
  readonly loadPanels: () => Promise<readonly PanelOption[]>
  readonly loadStatements: (panelId: string) => Promise<PanelStatementsData>
  /** Appendix B's `report_financial`. The routes check the same row. */
  readonly canIssue: boolean
  /** The branch's own today, so a month still being traded in can be labelled. */
  readonly today: string
  readonly timezone: string
  readonly onIssued?: () => void
}

const money = (value: WireMoney): string => formatWireForDisplay(value.minor, value.currency)

/**
 * `CLM-004`'s eight claim states as words. Appendix S, `LOC-001`.
 *
 * **Not the enum.** `written_off` and `partially_paid` on a table a payer is shown are not words
 * anybody writes, and no translator can reach an identifier: a shop running in Urdu would read two
 * English snake_case tokens in the middle of a statement. Every other status in this app goes
 * through `status-labels.ts` for the same reason, and this map belongs there once the claims
 * vertical has a second screen; it is local while it has one.
 *
 * `ARC-020`: the value arrives unnarrowed from the read layer, so a state this build does not know
 * shows as itself rather than as a blank cell, which is the shape `jobStatusWord` already takes.
 */
const CLAIM_STATUS: Readonly<Record<string, MessageKey>> = {
  captured: 'claimStatus.captured',
  submitted: 'claimStatus.submitted',
  partially_paid: 'claimStatus.partiallyPaid',
  denied: 'claimStatus.denied',
  paid: 'claimStatus.paid',
  written_off: 'claimStatus.writtenOff',
  transferred: 'claimStatus.transferred',
  reversed: 'claimStatus.reversed',
}

export const claimStatusWord = (t: Translate, value: string): string => {
  const key = CLAIM_STATUS[value]
  return key === undefined ? value : t(key)
}

/** The eight the domain declares are the eight above, so the map cannot fall behind the registry. */
export const CLAIM_STATUS_KEYS: readonly string[] = CLAIM_STATUSES

export function PanelStatements({
  loadPanels,
  loadStatements,
  canIssue,
  today,
  timezone,
  onIssued,
}: PanelStatementsProps): JSX.Element | null {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [panels, setPanels] = useState<readonly PanelOption[] | null>(null)
  const [panelsFailed, setPanelsFailed] = useState(false)
  const [panelId, setPanelId] = useState('')
  const [data, setData] = useState<PanelStatementsData | null>(null)
  const [failed, setFailed] = useState(false)
  const [month, setMonth] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void loadPanels()
      .then((rows) => {
        if (!cancelled) setPanels(rows)
      })
      .catch(() => {
        if (!cancelled) setPanelsFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [loadPanels])

  const refresh = useCallback(() => {
    if (panelId === '') {
      setData(null)
      return () => undefined
    }
    let cancelled = false
    setFailed(false)
    setData(null)
    void loadStatements(panelId)
      .then((answer) => {
        if (!cancelled) setData(answer)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [loadStatements, panelId])

  useEffect(() => refresh(), [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setConfirming(false)
    setMonth('')
    refresh()
    onIssued?.()
  })

  const chosen = data?.offers.find((offer) => offer.month === month)

  const issue = useCallback(async () => {
    if (chosen === undefined) return
    const write = (await enqueue({
      method: 'POST',
      path: `/panels/${encodeURIComponent(panelId)}/statements`,
      label: t('panelStatements.queueLabel', { month: chosen.month }),
      body: {
        month: chosen.month,
        // From the offer, never typed: this is the optimistic lock for a chain whose rows are
        // append-only, so the id has to be the one that was on screen.
        ...(chosen.supersedesStatementId === undefined
          ? {}
          : { supersedesStatementId: chosen.supersedesStatementId }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [chosen, enqueue, panelId, t])

  if (!canIssue) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.panelStatement'))
  const offered = (data?.offers ?? []).filter((offer) => offer.offered)
  const withheldMonths = (data?.offers ?? []).filter((offer) => !offer.offered)

  return (
    <section className="lm-screen" aria-labelledby="panel-statements-heading">
      <h1 id="panel-statements-heading" className="lm-screen__heading">
        {t('panelStatements.heading')}
      </h1>
      <p className="lm-hint">{t('panelStatements.whatItIs')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('panelStatements.panelLabel')}</span>
        <select
          className="lm-input"
          value={panelId}
          onChange={(e) => {
            setPanelId(e.currentTarget.value)
            setMonth('')
            setConfirming(false)
            setOpen(null)
          }}
        >
          <option value="">{t('panelStatements.panelUnchosen')}</option>
          {(panels ?? []).map((panel) => (
            <option key={panel.id} value={panel.id}>
              {panel.deactivated
                ? t('panelStatements.panelDeactivated', { panel: panel.name })
                : panel.name}
            </option>
          ))}
        </select>
      </label>
      {panelsFailed ? <p className="lm-error">{t('panelStatements.panelsFailed')}</p> : null}

      {panelId === '' ? (
        <p className="lm-hint">{t('panelStatements.chooseAPanel')}</p>
      ) : (
        <>
          <h2 className="lm-screen__subheading">{t('panelStatements.issuedHeading')}</h2>
          <DataSurface
            state={
              failed
                ? {
                    kind: 'error',
                    message: t('panelStatements.loadFailed'),
                    onRetry: () => {
                      refresh()
                    },
                  }
                : data === null
                  ? { kind: 'loading' }
                  : data.statements.length === 0
                    ? { kind: 'empty' }
                    : { kind: 'ready' }
            }
            shape="rows"
            rows={3}
            what={t('panelStatements.what')}
            emptyMessage={t('panelStatements.noneIssuedInvite')}
          >
            <ul className="lm-statement-list">
              {(data?.statements ?? []).map((statement) => (
                <li key={statement.id} className="lm-statement">
                  {/*
                    `LOC-004`: a period is two calendar dates and both are written the way the shop
                    writes one. One catalogue sentence with two holes rather than two `t()` calls
                    concatenated, per Appendix S.
                  */}
                  <span className="lm-statement__period">
                    {t('panelStatements.period', {
                      from: formatDay(statement.periodFrom, timezone, locale),
                      to: formatDay(statement.periodTo, timezone, locale),
                    })}
                  </span>
                  <span>{t('panelStatements.revision', { revision: statement.revision })}</span>
                  {statement.superseded ? (
                    <span className="lm-statement__superseded">
                      {t('panelStatements.superseded')}
                    </span>
                  ) : null}
                  <span>
                    {t('panelStatements.issuedOn', {
                      date: formatDay(statement.issuedAt, timezone, locale),
                    })}
                  </span>
                  <span className="lm-statement__total">{money(statement.totals.panelShare)}</span>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => setOpen(open === statement.id ? null : statement.id)}
                  >
                    {open === statement.id
                      ? t('panelStatements.hideLines')
                      : t('panelStatements.showLines', { count: statement.lines.length })}
                  </button>
                  {open === statement.id ? (
                    <table className="lm-statement__lines">
                      <caption>{t('panelStatements.linesCaption')}</caption>
                      <thead>
                        <tr>
                          <th scope="col">{t('panelStatements.colOrder')}</th>
                          <th scope="col">{t('panelStatements.colCaptured')}</th>
                          <th scope="col">{t('panelStatements.colStatus')}</th>
                          <th scope="col">{t('panelStatements.colPanelShare')}</th>
                          <th scope="col">{t('panelStatements.colPaid')}</th>
                          <th scope="col">{t('panelStatements.colOutstanding')}</th>
                          <th scope="col">{t('panelStatements.colDenied')}</th>
                          <th scope="col">{t('panelStatements.colTransferred')}</th>
                          <th scope="col">{t('panelStatements.colWrittenOff')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statement.lines.map((row) => (
                          <tr key={row.claimId}>
                            <td dir="ltr">
                              <Ltr>{row.orderId}</Ltr>
                            </td>
                            <td>{formatDay(row.capturedOn, timezone, locale)}</td>
                            <td>{claimStatusWord(t, row.claimStatus)}</td>
                            <td>{money(row.panelShare)}</td>
                            <td>{money(row.paid)}</td>
                            <td>{money(row.outstanding)}</td>
                            <td>{money(row.denied)}</td>
                            <td>{money(row.transferred)}</td>
                            <td>{money(row.writtenOff)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th scope="row" colSpan={3}>
                            {t('panelStatements.totalsRow')}
                          </th>
                          <td>{money(statement.totals.panelShare)}</td>
                          <td>{money(statement.totals.paid)}</td>
                          <td>{money(statement.totals.outstanding)}</td>
                          <td>{money(statement.totals.denied)}</td>
                          <td>{money(statement.totals.transferred)}</td>
                          <td>{money(statement.totals.writtenOff)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : null}
                </li>
              ))}
            </ul>
          </DataSurface>

          {data !== null && data.withheld > 0 ? (
            <p className="lm-hint lm-statements__withheld">
              {t('panelStatements.withheld', { count: data.withheld })}
            </p>
          ) : null}

          {/*
            Withheld until the statements have arrived. The chooser is built from the same read, so
            offered while it is loading or failed it would put months on screen whose offer nobody
            has checked, which is the refusal that stops the till.
          */}
          {data === null || failed ? null : (
            <>
              <h2 className="lm-screen__subheading">{t('panelStatements.issueHeading')}</h2>
              {offered.length === 0 ? (
                <p className="lm-empty">{t('panelStatements.nothingToIssue')}</p>
              ) : (
                <>
                  <label className="lm-field">
                    <span className="lm-field__label">{t('panelStatements.monthLabel')}</span>
                    <select
                      className="lm-input"
                      value={month}
                      onChange={(e) => {
                        setMonth(e.currentTarget.value)
                        setConfirming(false)
                      }}
                    >
                      <option value="">{t('panelStatements.monthUnchosen')}</option>
                      {offered.map((offer) => (
                        <option key={offer.month} value={offer.month}>
                          {offer.month}
                        </option>
                      ))}
                    </select>
                  </label>

                  {chosen === undefined ? null : (
                    <>
                      <p className="lm-hint">
                        {t('panelStatements.monthSummary', { count: chosen.claims })}
                        {chosen.month >= today.slice(0, 7)
                          ? ` ${t('panelStatements.stillTrading')}`
                          : ''}
                      </p>
                      {confirming ? (
                        <>
                          <p className="lm-field__warning">
                            {chosen.supersedesStatementId === undefined
                              ? t('panelStatements.confirmIssue', {
                                  panel: data.panel,
                                  month: chosen.month,
                                })
                              : t('panelStatements.confirmReissue', {
                                  panel: data.panel,
                                  month: chosen.month,
                                  revision: chosen.revision,
                                })}
                          </p>
                          <button
                            type="button"
                            className="lm-button lm-button--primary"
                            disabled={writeId !== null}
                            onClick={() => {
                              void issue()
                            }}
                          >
                            {t('panelStatements.confirmButton')}
                          </button>
                          <button
                            type="button"
                            className="lm-button lm-button--secondary"
                            onClick={() => setConfirming(false)}
                          >
                            {t('panelStatements.cancel')}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="lm-button lm-button--primary"
                          onClick={() => setConfirming(true)}
                        >
                          {chosen.supersedesStatementId === undefined
                            ? t('panelStatements.issue')
                            : t('panelStatements.reissue')}
                        </button>
                      )}
                    </>
                  )}
                </>
              )}

              {withheldMonths.length === 0 ? null : (
                <>
                  <h2 className="lm-screen__subheading">{t('panelStatements.withheldHeading')}</h2>
                  <ul className="lm-statement-withheld">
                    {withheldMonths.map((offer) => (
                      <li key={offer.month}>
                        <span dir="ltr">
                          <Ltr>{offer.month}</Ltr>
                        </span>
                        {/* The server's sentence, shown as sent: it names the branches or the period. */}
                        <span dir="auto">{offer.message ?? offer.code ?? ''}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}

          <p className="lm-queue-state" role="status">
            {line}
          </p>
        </>
      )}
    </section>
  )
}
